import React from 'react'
import ReactDOMServer from 'react-dom/server'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from 'helpers/styles/contextMenu'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ContextMenu from 'ContextMenu'
import { updateWireAction, deleteWireAction } from 'actions/entityActions'
import { updateCurrentEntityAction } from 'actions/applicationActions'
import { messageAction } from 'actions/messageActions'
import { DEFAULT_WIRE_OPTIONS, CAMERA_SELECTED_COLORS, WIRE_SNAP_ICON, ACCESS_WIRE_ICON } from 'helpers/globals'
import showContextMenu from 'helpers/showContextMenu'
import { distanceSquared } from 'helpers/walls/snapToWall'
import getMapProjection from 'helpers/map/getMapProjection'
import interpolate from 'helpers/map/interpolate'
import { makeUiPropSelector, makeMapRefSelector, makeWireTooltipOverlaySelector } from 'components/MultiImager/selectors'
import { makeWireSelectedSelector, makeWireStartDeviceSelector, makeWireStartDeviceSelectedSelector,
  makeWireEndDeviceSelector, makeShowWireSelector } from 'selectors/components'

const { addListener } = google.maps.event
const WIRE_SPLIT_DISTANCE = 10

const makeMapStateToProps = _ => {
  const wireSelectedSelector = makeWireSelectedSelector()
  const mapRefSelector = makeMapRefSelector()
  const drawingWallSelector = makeUiPropSelector('drawingWall')
  const drawingWireSelector = makeUiPropSelector('drawingWire')
  const wireStartDeviceSelector = makeWireStartDeviceSelector()
  const wireEndDeviceSelector = makeWireEndDeviceSelector()
  const wireStartDeviceSelectedSelector = makeWireStartDeviceSelectedSelector()
  const showWireSelector = makeShowWireSelector()
  const mapLoadedSelector = makeUiPropSelector('mapLoaded')
  const wireTooltipOverlaySelector = makeWireTooltipOverlaySelector()

  const mapStateToProps = (state, ownProps) => ({
    activeInUI: wireSelectedSelector(state, ownProps),
    map: mapRefSelector(state),
    drawing: drawingWallSelector(state) || drawingWireSelector(state),
    startDevice: wireStartDeviceSelector(state, ownProps),
    endDevice: wireEndDeviceSelector(state, ownProps),
    startDeviceSelected: wireStartDeviceSelectedSelector(state, ownProps),
    showWire: showWireSelector(state, ownProps),
    mapLoaded: mapLoadedSelector(state),
    wireTooltipOverlay: wireTooltipOverlaySelector(state)
  })
  return mapStateToProps
}

class Wire extends React.Component {
  constructor(props) {
    super(props)
    this.overlay = props.overlay
    this.dragListeners = []
    this.markers = []
    this.middleMarkers = []
    this.state = { dragging: false, projection: null }
    this.contextMenu = new ContextMenu(props.map, this.contextMenuOptions())
  }

  componentDidMount() {
    const { remove, props, props: { id, showTooltip, wireTooltipOverlay, updateWireAction, start, startDevice, end, endDevice, location,
      showWire, mapLoaded, map } } = this

    const path = this.overlay ? this.overlay.getPath() : new google.maps.MVCArray(location.map(l => new google.maps.LatLng(l)))
    path.setAt(0, new google.maps.LatLng(startDevice.location))
    path.setAt(path.length - 1, new google.maps.LatLng(endDevice.location))

    if (showTooltip && this.overlay && wireTooltipOverlay) {
      wireTooltipOverlay.showInfoText(interpolate(path.getAt(0), path.getAt(1), 0.5))
    }

    if (!this.overlay) this.overlay = new google.maps.Polyline(Object.assign({}, DEFAULT_WIRE_OPTIONS, end.type === 'doorController' && ACCESS_WIRE_ICON, props))
    this.overlay.setPath(path)
    this.overlay.setVisible(showWire)
    this.overlay.setMap(this.props.map)
    this.addEventListeners()
    this.addMarkers()

    if (start.type === 'camera' && startDevice.markers) this.addEndMarkerListeners(startDevice.markers.camera)
    else if (start.type !== 'camera' && startDevice.fillMarker) this.addEndMarkerListeners(startDevice.fillMarker)
    if (endDevice.fillMarker) this.addEndMarkerListeners(endDevice.fillMarker, false)

    if (mapLoaded) this.setState({ projection: getMapProjection(map) })

    updateWireAction(id, { overlay: this.overlay, remove, showTooltip: false })
  }

  componentWillReceiveProps(nextProps) {
    const { locked, start, startDevice, endDevice, activeInUI, startDeviceSelected, showWire, mapLoaded } = this.props
    if (nextProps.strokeColor !== this.overlay.strokeColor) this.overlay.setOptions({ strokeColor: nextProps.strokeColor })

    if (nextProps.startDevice && startDevice.color !== nextProps.startDevice.color) {
      const strokeColor = start.type === 'camera' ? CAMERA_SELECTED_COLORS[nextProps.startDevice.color] : nextProps.startDevice.color

      this.overlay.setOptions({ strokeColor })
      this.props.updateWireAction(this.props.id, { strokeColor })
    }

    if (nextProps.locked !== locked || nextProps.activeInUI !== activeInUI || nextProps.startDeviceSelected !== startDeviceSelected || nextProps.showWire !== showWire) {
      this.setMarkerVisibility(nextProps)
    }
    if (nextProps.showWire !== showWire) this.overlay.setVisible(nextProps.showWire)

    // add marker listeners
    if (start.type === 'camera' && nextProps.startDevice && nextProps.startDevice.markers && (!startDevice.markers || nextProps.startDevice.markers.camera !== startDevice.markers.camera)) {
      this.addEndMarkerListeners(nextProps.startDevice.markers.camera)
    } else if (start.type !== 'camera' && nextProps.startDevice && nextProps.startDevice.fillMarker && !startDevice.fillMarker) {
      this.addEndMarkerListeners(nextProps.startDevice.fillMarker)
    }

    if (nextProps.endDevice.fillMarker && !endDevice.fillMarker) this.addEndMarkerListeners(nextProps.endDevice.fillMarker, false)

    // update projection
    if (nextProps.mapLoaded && !mapLoaded) this.setState({ projection: getMapProjection(nextProps.map) })
  }

  setMarkerVisibility = props => {
    const { showWire, locked, activeInUI, startDeviceSelected } = props

    this.markers.forEach(wrapper => wrapper.marker.setVisible(showWire && !locked && (activeInUI || startDeviceSelected)))
    this.middleMarkers.forEach(wrapper => wrapper.marker.setVisible(showWire && !locked && (activeInUI || startDeviceSelected)))
  }

  setCurrentIfApplicable = _ =>
    !this.props.drawing && !this.props.activeInUI && this.props.updateCurrentEntityAction({ id: this.props.id, type: 'wire' })

  addEventListeners() {
    const { overlay, props: { id, updateWireAction, messageAction } } = this
    const update = _ => !this.state.dragging && updateWireAction(id, { location: overlay.getPath().getArray().map(pt => pt.toJSON()) })

    addListener(overlay.getPath(), 'set_at', update)
    addListener(overlay.getPath(), 'insert_at', update)
    addListener(overlay.getPath(), 'remove_at', update)

    overlay.addListener('mousedown', this.setCurrentIfApplicable)
    overlay.addListener('mousedown', _ => this.props.locked && messageAction('generalError', 'This cable is locked. Right click to unlock.'))
    overlay.addListener('contextmenu', showContextMenu(this.contextMenu))
    addListener(this.contextMenu, 'menu_item_selected', this.eventHandler)
  }

  handleDelete = wrapper => {
    const { overlay, markers, middleMarkers, updateMiddleMarkerPosition } = this
    const { id, marker } = wrapper

    overlay.getPath().removeAt(id + 1)
    marker.setMap(null)

    markers.splice(id, 1)
    const middleWrapper = middleMarkers.splice(id + 1, 1)
    middleWrapper[0].marker.setMap(null)

    for (let i = id; i < middleMarkers.length - 1; i++) {
      markers[i].id = i
      middleMarkers[i].id = i
    }
    middleMarkers[middleMarkers.length - 1].id = middleMarkers.length - 1
    updateMiddleMarkerPosition(id)
    this.props.wireTooltipOverlay && this.props.wireTooltipOverlay.showRemovalText(middleMarkers[id].marker.getPosition())
  }

  handleInsert = (e, wrapper, middleMarker) => {
    const { overlay, markers, middleMarkers, addMarkerListeners } = this
    const path = overlay.getPath()
    const id = wrapper.id

    // set up new main marker
    const newMarker = new google.maps.Marker({
      icon: { ...WIRE_SNAP_ICON, scale: 8, path: google.maps.SymbolPath.CIRCLE },
      position: e.latLng,
      draggable: true,
      raiseOnDrag: false,
      map: this.props.map
    })

    const newMarkerWrapper = { id, marker: newMarker }
    markers.splice(id, 0, newMarkerWrapper)
    addMarkerListeners(newMarkerWrapper)

    // set up new middle marker
    const newMiddleMarker = new google.maps.Marker({
      icon: { ...WIRE_SNAP_ICON, scale: 6, path: google.maps.SymbolPath.CIRCLE },
      position: interpolate(e.latLng, path.getAt(id + 2), 0.5),
      draggable: true,
      raiseOnDrag: false,
      map: this.props.map
    })

    const newMiddleMarkerWrapper = { id: id + 1, marker: newMiddleMarker }
    middleMarkers.splice(id + 1, 0, newMiddleMarkerWrapper)
    addMarkerListeners(newMiddleMarkerWrapper, true)

    // update existing marker and reset IDs
    middleMarker.setPosition(interpolate(path.getAt(id), path.getAt(id + 1), 0.5))
    for (let i = id + 1; i < middleMarkers.length - 1; i++) {
      markers[i].id = i
      middleMarkers[i].id = i
    }
    middleMarkers[middleMarkers.length - 1].id = middleMarkers.length - 1
  }

  updateMiddleMarkerPosition = id => {
    const path = this.overlay.getPath()
    this.middleMarkers[id].marker.setPosition(interpolate(path.getAt(id), path.getAt(id + 1), 0.5))
  }

  addMarkerListeners = (wrapper, middle = false) => {
    const { marker } = wrapper
    const { overlay, handleInsert, updateMiddleMarkerPosition, handleDelete } = this

    if (middle) {
      let startPixel, startPos, split = false

      marker.addListener('dragstart', e => {
        this.setState({ dragging: true })
        startPixel = this.state.projection.fromLatLngToContainerPixel(e.latLng)
        startPos = e.latLng
      })

      marker.addListener('drag', e => {
        if (split) return overlay.getPath().setAt(wrapper.id + 1, e.latLng)

        const currentPixel = this.state.projection.fromLatLngToContainerPixel(e.latLng)
        if (Math.sqrt(distanceSquared(startPixel.x, startPixel.y, currentPixel.x, currentPixel.y)) > WIRE_SPLIT_DISTANCE) {
          overlay.getPath().insertAt(wrapper.id + 1, e.latLng)
          split = true
        } else {
          marker.setPosition(startPos)
          return e.stop()
        }
      })

      marker.addListener('dragend', e => {
        if (split) {
          overlay.getPath().setAt(wrapper.id + 1, e.latLng)
          handleInsert(e, wrapper, marker)
          this.props.updateWireAction(this.props.id, { location: overlay.getPath().getArray().map(pt => pt.toJSON()) })
        } else {
          marker.setPosition(startPos)
        }

        this.setState({ dragging: false })
        split = false
        startPixel = null
        startPos = null
      })
    } else {
      marker.addListener('dragstart', _ => this.setState({ dragging: true }))
      marker.addListener('drag', e => overlay.getPath().setAt(wrapper.id + 1, e.latLng))

      marker.addListener('dragend', e => {
        overlay.getPath().setAt(wrapper.id + 1, e.latLng)
        updateMiddleMarkerPosition(wrapper.id)
        updateMiddleMarkerPosition(wrapper.id + 1)
        this.props.updateWireAction(this.props.id, { location: overlay.getPath().getArray().map(pt => pt.toJSON()) })
        this.setState({ dragging: false })
      })

      marker.addListener('contextmenu', _ => handleDelete(wrapper))
    }

    marker.addListener('mouseover', _ => marker.setIcon({ ...marker.getIcon(), fillOpacity: 0.8 }))
    marker.addListener('mouseout', _ => marker.setIcon({ ...marker.getIcon(), fillOpacity: 0.55 }))
  }

  addMarkers = _ => {
    const { overlay, addMarkerListeners, setMarkerVisibility } = this
    const path = overlay.getPath()

    for (let i = 0; i < path.getLength() - 1; i++) {
      const middleMarker = new google.maps.Marker({
        icon: { ...WIRE_SNAP_ICON, scale: 6, path: google.maps.SymbolPath.CIRCLE },
        position: interpolate(path.getAt(i), path.getAt(i + 1), 0.5),
        draggable: true,
        raiseOnDrag: false,
        map: this.props.map
      })
      const wrapper = { id: i, marker: middleMarker }

      addMarkerListeners(wrapper, true)
      this.middleMarkers.push(wrapper)
    }

    for (let i = 1; i < path.getLength() - 1; i++) {
      const marker = new google.maps.Marker({
        icon: { ...WIRE_SNAP_ICON, scale: 8, path: google.maps.SymbolPath.CIRCLE },
        position: path.getAt(i),
        draggable: true,
        raiseOnDrag: false,
        map: this.props.map
      })
      const wrapper = { id: i - 1, marker }

      addMarkerListeners(wrapper)
      this.markers.push(wrapper)
    }

    setMarkerVisibility(this.props)
  }

  addEndMarkerListeners = (marker, start = true) => {
    const { overlay, dragListeners, updateMiddleMarkerPosition, props: { id, updateWireAction } } = this

    dragListeners.push(addListener(marker, 'dragstart', _ => this.setState({ dragging: true })))
    dragListeners.push(addListener(marker, 'dragend', _ => {
      updateMiddleMarkerPosition(start ? 0 : this.middleMarkers.length - 1)
      this.setState({ dragging: false })
      updateWireAction(id, { location: overlay.getPath().getArray().map(pt => pt.toJSON()) })
    }))
    dragListeners.push(addListener(marker, 'drag', e => overlay.getPath().setAt(start ? 0 : overlay.getPath().length - 1, e.latLng)))
  }

  contextMenuOptions = _ => {
    const { classes } = this.props

    const menuItemBuilder = function() {
      const { locked, end } = this.props
      const lockItem = {
        className: classes.contextMenuItem,
        eventName: 'toggleLock',
        id: 'menu_item_toggle_lock',
        label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={locked ? 'unlock' : 'lock'} />)} ${locked ? 'Unlock' : 'Lock'} Cable`
      }

      if (end.type === 'doorController') return [lockItem]

      return [
        {
          className: classes.contextMenuItem,
          eventName: 'delete',
          id: 'menu_item_delete',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={['far', 'trash-alt']} />)} Delete Cable`
        },
        lockItem
      ]
    }

    return {
      classNames: {
        menu: classes.contextMenu,
        menuSeparator: classes.contextMenuSeparator
      },
      menuItems: menuItemBuilder.bind(this)
    }
  }

  eventHandler = (_, eventName) => {
    const { id, locked, startDevice, endDevice, messageAction, deleteWireAction, updateWireAction } = this.props

    switch (eventName) {
      case 'delete':
        if (locked) {
          messageAction('generalError', 'Cannot delete locked cable. Right click to unlock.')
          break
        } else if (startDevice.locked) {
          if (!window.confirm(`${startDevice.name} is locked, are you sure you want to delete this cable?`)) break
        } else if (endDevice.locked) {
          if (!window.confirm(`${endDevice.name} is locked, are you sure you want to delete this cable?`)) break
        }

        this.remove()
        deleteWireAction(id, true)
        break
      case 'toggleLock':
        updateWireAction(id, { locked: !locked })
        break
    }
    this.contextMenu.hide()
  }

  remove = _ => {
    this.dragListeners.forEach(listener => listener && listener.remove())
    this.markers.forEach(wrapper => wrapper.marker && wrapper.marker.setMap(null))
    this.middleMarkers.forEach(wrapper => wrapper.marker && wrapper.marker.setMap(null))
    this.overlay.setMap(null)
  }

  componentWillUnmount = _ => this.remove()

  shouldComponentUpdate() {
    return false
  }

  render() {
    return null
  }
}

export default connect(makeMapStateToProps, { updateWireAction, deleteWireAction, messageAction, updateCurrentEntityAction })(injectSheet(style)(Wire))



// WEBPACK FOOTER //
// ./src/components/Wire/index.js